@if (!loadingCapture) {
  <div class="capture-detail">
    @if (loadErrors.length > 0) {
      <div class="alert alert-danger d-flex align-items-center mt-3" role="alert">
        <div>
          @for (loadError of loadErrors; track loadError) {
            <div>{{ loadError }}</div>
          }
        </div>
        <button type="button" class="btn btn-sm btn-outline-danger ms-auto" (click)="onRetryClick()">Try again</button>
      </div>
      @if (!report) {
        <a routerLink="/capture-admin/captures/{{ captureId }}">&larr; Back</a>
      }
    }
    @if (!loadingReport && report) {
      <div class="capture-audit mt-3">
        <div class="d-flex align-items-center mb-3">
          <a routerLink="/capture-admin/captures/{{ captureId }}">&larr; Back</a>
          <span class="ms-auto small text-muted">Generated {{ report.generatedAt | date: 'MM/dd/yyyy hh:mm:ss a' }}</span>
          <button type="button" class="btn btn-sm btn-outline-secondary ms-2" (click)="onRefreshClick()">Refresh</button>
        </div>

        <div class="row g-3">
          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Capture Overview</div>
              <div class="card-body">
                <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: overview }" />
                @if (statusTimestamps.length > 0) {
                  <div class="subsection-label">Timestamps</div>
                  <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: statusTimestamps }" />
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Identifiers</div>
              <div class="card-body">
                <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: identifiers }" />
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Client Configuration Options</div>
              <div class="card-body">
                <ng-container *ngTemplateOutlet="namedValues; context: { $implicit: report.clientConfiguration.flags }" />
                <div class="subsection-label">Values</div>
                <ng-container *ngTemplateOutlet="namedValues; context: { $implicit: report.clientConfiguration.values }" />
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Key Transitioner Predicates</div>
              <div class="card-body">
                <ng-container *ngTemplateOutlet="namedValues; context: { $implicit: report.predicates }" />
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Provider</div>
              <div class="card-body">
                @if (!report.provider.id) {
                  <p class="text-muted fst-italic">No provider</p>
                }
                <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: provider }" />
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Verifying Patient Attachment</div>
              <div class="card-body">
                @if (report.verifyingPatientAttachment) {
                  <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: verifyingPatientAttachment }" />
                } @else {
                  <p class="text-muted fst-italic mb-0">No verifying patient attachment</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Client Prescription Matches</div>
              <div class="card-body">
                @if (report.clientPrescriptionMatches.matches.length > 0) {
                  <ng-container
                    *ngTemplateOutlet="labeledValues; context: { $implicit: [{ label: 'All Internal Referral', value: report.clientPrescriptionMatches.allInternalReferral }] }" />
                  <table class="table table-sm table-striped mb-0 mt-2">
                    <thead><tr><th>ID</th><th>Match Type</th></tr></thead>
                    <tbody>
                      @for (match of report.clientPrescriptionMatches.matches; track match.id) {
                        <tr><td class="mono">{{ match.id }}</td><td class="mono">{{ match.matchType }}</td></tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0">None</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Referral Match Verifications</div>
              <div class="card-body">
                <ng-container
                  *ngTemplateOutlet="labeledValues; context: { $implicit: [
                    { label: 'Has Valid Referral Match Verification', value: report.referralMatchVerifications.hasValidReferralMatchVerification },
                    { label: 'Has Valid Referral Match Verification Or Substitute', value: report.referralMatchVerifications.hasValidReferralMatchVerificationOrSubstitute },
                    { label: 'Count', value: report.referralMatchVerifications.totalCount }
                  ] }" />
                @if (report.referralMatchVerifications.items.length > 0) {
                  <table class="table table-sm table-striped mb-0 mt-2">
                    <thead><tr><th>ID</th><th>Subtype</th></tr></thead>
                    <tbody>
                      @for (verification of report.referralMatchVerifications.items; track verification.id) {
                        <tr><td class="mono">{{ verification.id }}</td><td class="mono">{{ verification.subtype }}</td></tr>
                      }
                    </tbody>
                  </table>
                  @if (report.referralMatchVerifications.totalCount > report.referralMatchVerifications.items.length) {
                    <p class="small text-muted mb-0 mt-1">
                      Showing {{ report.referralMatchVerifications.items.length }} of {{ report.referralMatchVerifications.totalCount }}
                    </p>
                  }
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Encounters Eligible for Verification</div>
              <div class="card-body">
                @if (report.encounters.items.length > 0) {
                  <table class="table table-sm table-striped mb-0">
                    <thead>
                      <tr><th>ID</th><th>Date</th><th>Type</th><th>EHR Identifier</th><th>Location</th></tr>
                    </thead>
                    <tbody>
                      @for (encounter of report.encounters.items; track encounter.id) {
                        <tr>
                          <td class="mono">{{ encounter.id }}</td>
                          <td class="mono text-nowrap">{{ encounter.encounterDate | date: 'MM/dd/yyyy' }}</td>
                          <td>{{ encounter.encounterType }}</td>
                          <td class="mono">{{ encounter.ehrIdentifier }}</td>
                          <td>{{ encounter.patientInteractionLocation }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                  @if (report.encounters.totalCount > report.encounters.items.length) {
                    <p class="small text-muted mb-0 mt-1">
                      Showing {{ report.encounters.items.length }} of {{ report.encounters.totalCount }}
                    </p>
                  }
                } @else {
                  <p class="text-muted fst-italic mb-0">None, so the patient is not under the care of the CE</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Pool State (Patient + Prescriber NPI)</div>
              <div class="card-body">
                @if (report.pool.length > 0) {
                  <table class="table table-sm table-striped mb-0">
                    <thead><tr><th>Status</th><th class="text-end">Count</th></tr></thead>
                    <tbody>
                      @for (poolStatus of report.pool; track poolStatus.status) {
                        <tr>
                          <td>{{ poolStatus.status | captureStatus }}</td>
                          <td class="text-end mono">{{ poolStatus.count }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0">Empty pool</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Expiration</div>
              <div class="card-body">
                <ng-container
                  *ngTemplateOutlet="labeledValues; context: { $implicit: [
                    { label: 'Expiration Type', value: report.expiration.expirationType },
                    { label: 'Expired', value: report.expiration.expired }
                  ] }" />
                @if (report.expiration.records.length > 0) {
                  <table class="table table-sm table-striped mb-0 mt-2">
                    <thead><tr><th>Time</th><th>Previous Status</th><th>Previous Reason</th></tr></thead>
                    <tbody>
                      @for (record of report.expiration.records; track $index) {
                        <tr>
                          <td class="mono text-nowrap">{{ record.createdAt | date: 'MM/dd/yyyy hh:mm a' }}</td>
                          <td>{{ record.previousStatus | captureStatus }}</td>
                          <td>{{ record.previousStatusReason | captureStatusReason }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0 mt-2">No expiration records</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-header">Capture Errors</div>
              <div class="card-body">
                @if (report.errors.length > 0) {
                  <table class="table table-sm table-striped mb-0">
                    <thead><tr><th>Time</th><th>Message</th></tr></thead>
                    <tbody>
                      @for (error of report.errors; track $index) {
                        <tr>
                          <td class="mono text-nowrap">{{ error.createdAt | date: 'MM/dd/yyyy hh:mm a' }}</td>
                          <td class="phi">{{ error.message }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0">None</p>
                }
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="card">
              <div class="card-header d-flex align-items-center">
                Transitioners
                <span class="legend ms-auto d-flex align-items-center gap-3">
                  <span class="d-flex align-items-center gap-1">
                    <span class="legend-swatch bg-success-subtle"></span>
                    Would transition if auto advance ran now
                  </span>
                  <span class="d-flex align-items-center gap-1">
                    <span class="legend-swatch bg-warning-subtle"></span>
                    Targets the current status
                  </span>
                  <span class="d-flex align-items-center gap-1">
                    <span class="legend-swatch unknown-swatch"></span>
                    Unknown, depends on the transition above
                  </span>
                </span>
              </div>
              <div class="card-body">
                <ng-container
                  *ngTemplateOutlet="labeledValues; context: { $implicit: [{ label: 'Pipeline', value: report.transitioners.pipeline }] }" />

                <div class="subsection-label">
                  Transitioners targeting current status ({{ report.capture.status | captureStatus }})
                </div>
                @if (report.transitioners.targetingCurrentStatus.length > 0) {
                  <ul class="mb-0">
                    @for (name of report.transitioners.targetingCurrentStatus; track name) {
                      <li class="mono">{{ name }}</li>
                    }
                  </ul>
                } @else {
                  <p class="text-muted fst-italic mb-0">None match the current status</p>
                }

                @if (report.transitioners.eligibility) {
                  <div class="subsection-label">
                    Eligibility if auto advance ran now
                    <span class="note">(evaluated in a rolled back transaction, no changes are saved)</span>
                  </div>
                  <table class="table table-sm mb-0">
                    <thead>
                      <tr><th>#</th><th>Transitioner</th><th>Target Status</th><th>Result</th></tr>
                    </thead>
                    <tbody>
                      @for (row of report.transitioners.eligibility; track row.position) {
                        <tr [class]="eligibilityRowClass(row)">
                          <td class="mono">{{ row.position }}</td>
                          <td class="mono">{{ row.name }}</td>
                          <td class="mono">{{ row.targetStatus }}</td>
                          <td>
                            @switch (row.result) {
                              @case ('error') {
                                <span class="mono text-danger">ERROR: {{ row.error }}</span>
                              }
                              @case ('would_transition') {
                                <span class="badge text-bg-success">WOULD TRANSITION</span>
                              }
                              @case ('unknown') {
                                <span>unknown</span>
                              }
                              @default {
                                <span class="text-muted">no</span>
                              }
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0 mt-2">Terminal capture, forward eligibility is not applicable</p>
                }
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="card">
              <div class="card-header">Timeline (audits and history items, chronological)</div>
              <div class="card-body">
                @if (report.timeline.length > 0) {
                  <table class="table table-sm mb-0 timeline">
                    <thead><tr><th>Time</th><th>Source</th><th>Detail</th><th>User</th></tr></thead>
                    <tbody>
                      @for (row of report.timeline; track $index) {
                        <tr [class]="row.source + '-row'">
                          <td class="mono text-nowrap">{{ row.at | date: 'MM/dd/yyyy hh:mm:ss a' }}</td>
                          <td>
                            <span class="badge" [class]="row.source === 'audit' ? 'text-bg-warning' : 'text-bg-info'">
                              {{ row.source.toUpperCase() }}
                            </span>
                          </td>
                          <td class="mono">
                            @if (row.changes.length > 0) {
                              @for (change of row.changes; track change.field) {
                                <div>{{ change.field }}: {{ change.from ?? '(none)' }} &rarr; {{ change.to ?? '(none)' }}</div>
                              }
                            } @else {
                              {{ row.action }}
                            }
                          </td>
                          <td class="text-nowrap text-muted">{{ row.user ?? 'system' }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0">No records</p>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    } @else if (loadingReport) {
      <mat-spinner />
    }
  </div>
} @else {
  <mat-spinner />
}

<!-- label / value rows for objects converted with toLabeledValues -->
<ng-template #labeledValues let-rows>
  <table class="table table-sm table-borderless kv mb-0">
    <tbody>
      @for (row of rows; track row.label) {
        <tr>
          <td class="label">{{ row.label }}</td>
          <td class="mono">
            @if (row.link) {
              <a [routerLink]="row.link">{{ row.value }}</a>
            } @else {
              <ng-container *ngTemplateOutlet="value; context: { $implicit: row.value, isDate: row.isDate }" />
            }
          </td>
        </tr>
      }
    </tbody>
  </table>
</ng-template>

<!-- name / value rows where the name is a code identifier (configuration option or predicate) -->
<ng-template #namedValues let-rows>
  <table class="table table-sm table-borderless kv mb-0">
    <tbody>
      @for (row of rows; track row.name) {
        <tr>
          <td class="label mono">{{ row.name }}</td>
          <td class="mono">
            <ng-container *ngTemplateOutlet="value; context: { $implicit: row.value, isDate: false }" />
          </td>
        </tr>
      }
    </tbody>
  </table>
</ng-template>

<ng-template #value let-value let-isDate="isDate">
  @if (isBoolean(value)) {
    <span class="badge" [class]="value ? 'text-bg-success' : 'text-bg-light text-muted'">{{ value ? 'YES' : 'no' }}</span>
  } @else if (isBlank(value)) {
    <span class="text-muted">--</span>
  } @else if (isObject(value)) {
    @for (entry of value | keyvalue; track entry.key) {
      <div>{{ entry.key }}: {{ entry.value | json }}</div>
    }
  } @else if (isDate) {
    {{ value | date: (value.length > 10 ? 'MM/dd/yyyy hh:mm:ss a' : 'MM/dd/yyyy') }}
  } @else {
    {{ value }}
  }
</ng-template>
